import ArticleLayout from './ArticleLayout'
import { codexWindowsFaqItems } from './seo/codexWindowsPage'
import { bookingLabel, codexWindowsWhatsAppMessage, individualBookingUrl, valoresPath, whatsAppUrlFor, youtubeChannelUrl } from './siteDestinations'
import { ArticleClosing, ArticleFaq, ArticleList, ArticleSection } from './ui/Article'

/**
 * Página "Como instalar o Codex no Windows" (/como-instalar-o-codex-no-windows/).
 * Escrita para quem trabalha no administrativo e não é da área: o básico do
 * básico, sem termo técnico. Passo a passo conferido na máquina de Arthur em
 * 11/09/2026 (aplicativo ChatGPT da loja da Microsoft, menus em português).
 * As perguntas do fim vêm de src/seo/codexWindowsPage.ts (teste).
 */
export default function CodexWindowsPage() {
  return (
    <ArticleLayout
      eyebrow="Passo a passo"
      title="Codex no Windows"
      intro="O Codex é uma parte do ChatGPT que trabalha nos arquivos de uma pasta do seu computador. Você escreve o que quer, em português, e ele faz. Esta página mostra como colocar isso no seu computador, um passo de cada vez."
      note="Escrito e conferido no meu computador em 11 de setembro de 2026. Telas, planos e valores da OpenAI mudam com o tempo: confira no dia em que você ler."
      rogerio={{ pose: 'conferindo', text: 'Eu supervisiono a instalação. Principalmente as pausas.' }}
      facts={[[ 'Windows', 'o computador deste passo a passo' ], [ '1 conta', 'a sua conta do ChatGPT' ], [ '1 pasta', 'com os arquivos da primeira tarefa' ], [ 'Português', 'para pedir e ajustar o resultado' ]]}
      flow={['instalar', 'entrar', 'escolher a pasta', 'pedir em português', 'conferir', 'guardar', 'retomar']}
      secondary={{ href: '#codexwin-antes', label: 'Ler o passo a passo' }}
    >
      <ArticleSection id="codexwin-antes" title="O que você precisa">
        <ArticleList>
          <li>Um computador com Windows.</li>
          <li>Uma conta do ChatGPT. Se você já usa o ChatGPT no site ou no celular, é essa. Se não tem, crie uma em chatgpt.com: é gratuita.</li>
          <li>Uns dez minutos.</li>
        </ArticleList>
        <p>Tudo é feito clicando e escrevendo em português.</p>
      </ArticleSection>

      <ArticleSection id="codexwin-1" title="1. Instalar">
        <ArticleList ordered>
          <li>Clique no botão Iniciar do Windows, escreva <strong>Microsoft Store</strong> e abra a loja.</li>
          <li>Na busca da loja, escreva <strong>ChatGPT</strong> e abra o resultado da OpenAI.</li>
          <li>Clique em <strong>Instalar</strong> e espere terminar.</li>
        </ArticleList>
        <p>
          O nome na loja é ChatGPT: o Codex está dentro dele. Se preferir, abra <a href="https://apps.microsoft.com/detail/9PLM9XGG6VKS" target="_blank" rel="noopener noreferrer">a página do ChatGPT na loja</a> e clique em Instalar.
        </p>
      </ArticleSection>

      <ArticleSection id="codexwin-2" title="2. Entrar">
        <p>
          Abra o aplicativo <strong>ChatGPT</strong> pelo menu Iniciar e clique em Entrar. Use a mesma conta do ChatGPT
          que você já usa. Uma janela do navegador abre para confirmar e volta sozinha para o aplicativo.
        </p>
        <p>
          Sobre pagar: em 11 de setembro de 2026, a OpenAI dizia que o Codex vem em todos os planos, inclusive no
          gratuito. Já o aplicativo para o computador, este que trabalha na pasta, aparecia na lista de planos a partir
          do Plus. Isso muda com o tempo, então confira na página de planos da OpenAI no dia em que você ler. Se o
          aplicativo pedir um plano, é esse.
        </p>
      </ArticleSection>

      <ArticleSection id="codexwin-3" title="3. Escolher o Codex">
        <p>
          No canto de cima, à esquerda, tem um botão escrito <strong>ChatGPT</strong> ou <strong>Codex</strong>. Clique
          nele e escolha <strong>Codex</strong>. O ChatGPT é a conversa comum; o Codex é o que trabalha na pasta.
        </p>
      </ArticleSection>

      <ArticleSection id="codexwin-4" title="4. Criar um projeto numa pasta">
        <p>Um projeto é só uma pasta do seu computador que o Codex pode ver.</p>
        <ArticleList ordered>
          <li>Crie uma pasta nova, por exemplo <strong>Primeiro projeto</strong>, na área de trabalho, e ponha nela os arquivos da tarefa.</li>
          <li>No aplicativo, na lista da esquerda, ache <strong>Projetos</strong> e clique no sinal de <strong>+</strong> ao lado.</li>
          <li>Escolha <strong>Local</strong> e clique em <strong>Avançar</strong>.</li>
          <li>Escolha a pasta que você criou. Pronto: ela aparece na lista de projetos.</li>
        </ArticleList>
      </ArticleSection>

      <ArticleSection id="codexwin-5" title="5. Pedir em português">
        <p>
          A tela mostra uma caixa escrita <strong>Peça qualquer coisa</strong>. Escreva como falaria para um colega:
          o que tem na pasta, o que você quer no fim e o que fazer se ele ficar na dúvida.
        </p>
        <p>
          Exemplo: "Nesta pasta tem uma planilha com nomes e telefones, alguns repetidos. Quero a mesma lista sem
          repetidos, com os telefones escritos do mesmo jeito, e uma aba separada com o que ficou em dúvida."
        </p>
        <p>
          Embaixo da caixa está escrito <strong>Aprovar por mim</strong>. Deixe assim: ele pergunta antes de fazer
          qualquer coisa arriscada.
        </p>
      </ArticleSection>

      <ArticleSection id="codexwin-6" title="6. Conferir">
        <p>
          Quando ele terminar, os arquivos novos aparecem na pasta. Abra e confira um por um antes de usar: ele pode se
          enganar, e quem responde pelo resultado é você. Se ele perguntar alguma coisa, responda na mesma caixa. Depois,
          guarde o seu pedido num arquivo de texto dentro da pasta, para repetir na semana que vem só trocando os
          arquivos.
        </p>
      </ArticleSection>

      <ArticleSection id="codexwin-diferente" title="Se acontecer diferente">
        <ArticleList>
          <li><strong>A loja pediu uma conta da Microsoft.</strong> É a conta do próprio Windows. Entre com ela e procure ChatGPT de novo.</li>
          <li><strong>Não acho Projetos.</strong> Olhe o botão do canto de cima, à esquerda: precisa estar em Codex.</li>
          <li><strong>Pediu um plano.</strong> Veja o aviso sobre pagar, no passo 2.</li>
          <li><strong>Ele disse que falta um arquivo.</strong> Ponha o arquivo na pasta e responda "coloquei o arquivo na pasta".</li>
        </ArticleList>
      </ArticleSection>

      <ArticleSection id="codexwin-faq" eyebrow="Ainda tem alguma ponta solta?" title="Dúvidas">
        <ArticleFaq idPrefix="codexwin-faq" items={codexWindowsFaqItems} />
      </ArticleSection>

      <ArticleClosing
        eyebrow="Instalou. E agora?"
        title="A primeira tarefa de verdade, com alguém do lado."
        action={{ href: individualBookingUrl, label: bookingLabel, external: true }}
        secondary={[
          { href: valoresPath, label: 'Ver os valores' },
          { href: youtubeChannelUrl, label: 'Ver o canal no YouTube', external: true },
        ]}
        note={<>Você escolhe o horário na minha agenda. Formato, duração e investimento do acompanhamento a gente combina na conversa. <a href={whatsAppUrlFor(codexWindowsWhatsAppMessage)} target="_blank" rel="noopener noreferrer">Prefere tirar uma dúvida antes? Me chame no WhatsApp ↗</a></>}
        rogerio={{ pose: 'concluido', text: 'Eu cuido do novelo. Você faz o primeiro pedido.' }}
      >
        Converse comigo sobre a tarefa que você quer tirar da frente: em 30 minutos, a gente descobre como usar IA
        no seu caso, e a primeira conversa é gratuita. No canal eu mostro uma tarefa por semana, com a pessoa pedindo
        do começo ao fim.
      </ArticleClosing>
    </ArticleLayout>
  )
}
